import { createLogger } from '../core/logger.js';
import { redactUrl } from '../core/url.js';
export const DEFAULT_MAX_BATCH = 20;
export const DEFAULT_MAX_BUFFER = 200;
export const DEFAULT_FLUSH_INTERVAL_MS = 5000;
/**
 * The most a batch's body may weigh, in UTF-8 bytes.
 *
 * Browsers refuse a `keepalive` request (and a beacon) once the bodies they are
 * holding pass 64 KiB, and the logger drops a refused batch. Twenty errors with
 * long stacks already pass that, so a batch is cut by size as well as count,
 * with room left under the limit for headers and a second request in flight.
 */
export const MAX_KEEPALIVE_BYTES = 60 * 1024;
const encoder = new TextEncoder();
const byteLength = (text) => encoder.encode(text).length;
/** Read what the environment happens to offer, without assuming any of it. */
export function detectBrowserGlobals() {
    const scope = globalThis;
    const navigator = scope.navigator;
    const beacon = navigator?.sendBeacon;
    const randomUUID = scope.crypto?.randomUUID;
    return {
        ...(typeof scope.fetch === 'function'
            ? { fetch: scope.fetch.bind(scope) }
            : {}),
        // Bound to `navigator`: detached, `sendBeacon` throws "illegal invocation".
        ...(typeof beacon === 'function'
            ? { sendBeacon: beacon.bind(navigator) }
            : {}),
        ...(typeof scope.addEventListener === 'function'
            ? { addEventListener: scope.addEventListener.bind(scope) }
            : {}),
        ...(typeof scope.removeEventListener === 'function'
            ? {
                removeEventListener: scope.removeEventListener.bind(scope),
            }
            : {}),
        ...(typeof scope.setInterval === 'function'
            ? { setInterval: scope.setInterval.bind(scope) }
            : {}),
        ...(typeof scope.clearInterval === 'function'
            ? { clearInterval: scope.clearInterval.bind(scope) }
            : {}),
        ...(scope.location === undefined
            ? {}
            : { currentUrl: () => (typeof scope.location?.href === 'string' ? scope.location.href : undefined) }),
        ...(typeof navigator?.userAgent === 'string' ? { userAgent: navigator.userAgent } : {}),
        ...(typeof randomUUID === 'function'
            ? { randomId: randomUUID.bind(scope.crypto) }
            : {}),
    };
}
/**
 * A logger that ships to an ingest endpoint.
 *
 * The returned object is an ordinary {@link Logger}: the same four methods, the
 * same `child()`, the same redaction. Only the sink differs, which is the point
 * of the sink abstraction.
 */
export function createBrowserLogger(options) {
    const globals = options.globals ?? detectBrowserGlobals();
    const maxBatch = options.maxBatch ?? DEFAULT_MAX_BATCH;
    const maxBuffer = options.maxBuffer ?? DEFAULT_MAX_BUFFER;
    const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    const captureUrl = options.captureUrl !== false;
    const urlRedaction = options.urlRedaction ?? {};
    const onError = options.onError ?? (() => { });
    let buffer = [];
    let dropped = 0;
    let inFlight = Promise.resolve();
    let closed = false;
    async function send(records, beacon) {
        if (records.length === 0)
            return;
        const body = JSON.stringify({ records });
        // Closing pages get the beacon, which the browser owns and will deliver
        // even after the document is gone. Everything else gets a real request, so
        // failures are visible.
        if (beacon && globals.sendBeacon !== undefined) {
            if (globals.sendBeacon(options.endpoint, body))
                return;
            // A refused beacon means the queue is full; falling through to `fetch`
            // is the only remaining chance of delivery.
        }
        if (globals.fetch === undefined) {
            onError(new Error('No fetch available to deliver log records.'));
            return;
        }
        const extra = typeof options.headers === 'function' ? await options.headers() : (options.headers ?? {});
        const response = await globals.fetch(options.endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...extra },
            body,
            // A single record too big for the keepalive quota still goes, as an
            // ordinary request: refused outright, it would be lost.
            keepalive: byteLength(body) <= MAX_KEEPALIVE_BYTES,
            ...(options.credentials === undefined ? {} : { credentials: options.credentials }),
        });
        if (!response.ok) {
            throw new Error(`Log ingest responded ${response.status}.`);
        }
    }
    /** The next batch: at most `maxBatch` records, and at most {@link MAX_KEEPALIVE_BYTES} of them. */
    function take() {
        const batch = [];
        let bytes = '{"records":[]}'.length;
        while (buffer.length > 0 && batch.length < maxBatch) {
            const size = byteLength(JSON.stringify(buffer[0])) + 1;
            // Always take one, however large, so an oversized record can't block the queue.
            if (batch.length > 0 && bytes + size > MAX_KEEPALIVE_BYTES)
                break;
            batch.push(buffer.shift());
            bytes += size;
        }
        // Say how many were lost, once, on the next batch that gets through —
        // silence would make a truncated log look like a quiet period.
        if (dropped > 0 && batch[0] !== undefined) {
            batch[0] = { ...batch[0], fields: { ...batch[0].fields, droppedRecords: dropped } };
            dropped = 0;
        }
        return batch;
    }
    /** Send batch after batch until the buffer is empty — including what arrives meanwhile. */
    async function sendAll(beacon) {
        while (buffer.length > 0) {
            try {
                await send(take(), beacon);
            }
            catch (error) {
                onError(error);
            }
        }
    }
    function drain(beacon = false) {
        if (buffer.length === 0)
            return inFlight;
        // One send loop at a time, queued behind the last. The loop keeps going
        // until the buffer is empty, so it never chains onto itself: an earlier
        // version continued by calling `drain` from inside `inFlight`'s own
        // continuation, which made `inFlight` wait on a promise that waited on it —
        // and every later record queued behind a promise that never settled.
        inFlight = inFlight.then(() => sendAll(beacon));
        return inFlight;
    }
    const sink = {
        write(records) {
            buffer.push(...records);
            if (buffer.length > maxBuffer) {
                // Oldest first. A page that has been open for an hour is far more
                // likely to be debugged from what just happened than from what
                // happened at the start.
                dropped += buffer.length - maxBuffer;
                buffer = buffer.slice(buffer.length - maxBuffer);
            }
            if (buffer.length >= maxBatch)
                void drain();
        },
        async flush() {
            await drain();
            await inFlight;
        },
    };
    const sessionId = options.sessionId ?? globals.randomId?.();
    const logger = createLogger({
        source: options.source,
        sink,
        origin: 'browser',
        ...(options.level === undefined ? {} : { level: options.level }),
        ...(options.environment === undefined ? {} : { environment: options.environment }),
        ...(options.clock === undefined ? {} : { clock: options.clock }),
        ...(options.redact === undefined ? {} : { redact: options.redact }),
        onError,
        context: {
            ...options.context,
            ...(sessionId === undefined ? {} : { sessionId }),
        },
        // Resolved per record rather than captured once: a single-page app changes
        // its URL without ever creating a new logger.
        //
        // Redacted, because a page URL is one of the likeliest places for a
        // credential to be sitting in plain sight — `#access_token=` after an
        // implicit-flow sign-in, `?code=` on the way back from a provider, a
        // password-reset `?token=`. Key-based redaction cannot help: the key is
        // `url` and the secret is in the value.
        ...(captureUrl && globals.currentUrl !== undefined
            ? {
                ambientContext: () => {
                    const url = globals.currentUrl?.();
                    return url === undefined ? {} : { fields: { url: redactUrl(url, urlRedaction) } };
                },
            }
            : {}),
    });
    const timer = flushIntervalMs > 0 && globals.setInterval !== undefined
        ? globals.setInterval(() => void drain(), flushIntervalMs)
        : undefined;
    // `pagehide` rather than `unload`: `unload` is unreliable on mobile and
    // disqualifies the page from the back/forward cache. `visibilitychange` picks
    // up the tab-switch case, where the page may never be brought back.
    const onHide = () => {
        void drain(true);
    };
    if (globals.addEventListener !== undefined) {
        globals.addEventListener('pagehide', onHide);
        globals.addEventListener('visibilitychange', onHide);
    }
    return {
        ...logger,
        async close() {
            if (closed)
                return;
            closed = true;
            if (timer !== undefined)
                globals.clearInterval?.(timer);
            globals.removeEventListener?.('pagehide', onHide);
            globals.removeEventListener?.('visibilitychange', onHide);
            await logger.flush();
        },
    };
}
/**
 * Report what the page never told anyone about.
 *
 * Uncaught errors and unhandled rejections are the two failures a frontend has
 * no other way to learn about: nobody files a ticket saying "a promise rejected
 * in a component I never opened". Returns a function that removes the listeners
 * again, which matters in tests and in a page that mounts more than once.
 */
export function installGlobalErrorCapture(logger, globals = detectBrowserGlobals()) {
    const onErrorEvent = (event) => {
        const detail = event;
        logger.log({
            level: 'error',
            event: 'browser.uncaught',
            ...(typeof detail?.message === 'string' ? { message: detail.message } : {}),
            ...(detail?.error === undefined ? {} : { error: detail.error }),
            fields: {
                ...(typeof detail?.filename === 'string' ? { file: detail.filename } : {}),
                ...(typeof detail?.lineno === 'number' ? { line: detail.lineno } : {}),
                ...(typeof detail?.colno === 'number' ? { column: detail.colno } : {}),
            },
        });
    };
    const onRejection = (event) => {
        const detail = event;
        logger.log({
            level: 'error',
            event: 'browser.unhandled-rejection',
            error: detail?.reason,
        });
    };
    globals.addEventListener?.('error', onErrorEvent);
    globals.addEventListener?.('unhandledrejection', onRejection);
    return () => {
        globals.removeEventListener?.('error', onErrorEvent);
        globals.removeEventListener?.('unhandledrejection', onRejection);
    };
}
//# sourceMappingURL=logger.js.map